import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Link } from 'react-router-dom';

import { cn } from 'helpers/classnames';
import { pluralize } from 'helpers';
import { extractReturnableOrders } from 'helpers/MyAccountUtils';
import SiteAwareMetadata from 'components/SiteAwareMetadata';
import { PageLoader } from 'components/Loader';
import AccountBreadcrumbs from 'components/account/AccountBreadcrumbs';
import ReturnProductCards from 'components/account/Returns/ReturnProductCards';
import FloatingReturnItemList from 'components/account/Returns/FloatingReturnItemList';
import { fetchOrders } from 'actions/account/orders';
import { removeItemForCancellationOrReturn, selectItemForCancellationOrReturn } from 'actions/account/account';
import { resetReturnsInState, stepSelection, updateBoxNumInfo, updateItemInBoxInfo } from 'actions/account/returns';
import { setIsShipmentOptionsLoading } from 'actions/account/order';
import { track } from 'apis/amethyst';
import { evNumberOfBoxesOnReturnSubmit } from 'events/returns';

// eslint-disable-next-line css-modules/no-unused-class
import returnCss from 'styles/containers/returns/returns.scss';
import css from 'styles/containers/account/returnAll.scss';

const RETURN_MAX_BOXES = 5;

export class ReturnAll extends Component {
  static contextTypes = {
    router: PropTypes.object.isRequired,
    marketplace: PropTypes.object,
    testId: PropTypes.func
  };

  state = {
    boxNum: 0 // default to 0 box to enforce the user to make a choice
  };
  componentDidMount() {
    const {
      account: { cancelOrReturnItems: items },
      returns: { boxInfo },
      fetchOrders,
      updateBoxNumInfo,
      stepSelection,
      showBatchReturn
    } = this.props;

    const {
      marketplace: {
        account: { crossOrderReturns }
      }
    } = this.context;

    if (!crossOrderReturns || !showBatchReturn) {
      this.context.router.push('/account');
    }

    if (!items.length) {
      stepSelection('SELECT_RETURN_ITEMS');
    }

    fetchOrders({ pageSize: 20 }, { returnTo: '/returns' });

    resetReturnsInState(); // when user clicks back button for example, clear returns state

    updateBoxNumInfo(0, boxInfo, items); // initialize 0 box default in store (navigated and selected from order history)
  }

  onReturnChecked = ({ item, orderId }) => {
    const {
      account: { cancelOrReturnItems: items },
      orders: { orders },
      selectItemForCancellationOrReturn,
      removeItemForCancellationOrReturn
    } = this.props;

    const { itemUniqueId } = item;

    const order = orders.filter(order => order.orderId === orderId);
    const orderItem = order[0].lineItems.filter(item => item.itemUniqueId === itemUniqueId);
    // if it exists in return items, remove, otherwise, add

    if (items.find(item => item.itemUniqueId === orderItem[0].itemUniqueId)) {
      removeItemForCancellationOrReturn(orderItem[0]);
    } else {
      orderItem[0].orderId = orderId;
      selectItemForCancellationOrReturn(orderItem[0]);
    }
  };

  onCancelReturn = () => {
    this.context.router.pushPreserveAppRoot('/account');
  };

  onNavigateToBoxSelection = () => {
    const {
      account: { cancelOrReturnItems: items },
      returns: { boxInfo },
      stepSelection,
      updateBoxNumInfo,
      setIsShipmentOptionsLoading
    } = this.props;
    const {
      marketplace: {
        account: { multiBoxReturns }
      }
    } = this.context;
    window.scrollTo(0, 0);
    if (items.length === 1 || multiBoxReturns === false) {
      updateBoxNumInfo(1, boxInfo, items); // initialize 1 box default in store (navigated from 'Return Items' to standard return)
      stepSelection('SELECT_LABEL');
      setIsShipmentOptionsLoading(true);
      this.context.router.pushPreserveAppRoot('/return');
    } else {
      updateBoxNumInfo(0, boxInfo, items); // initialize 0 box default in store (navigated from 'Return Items' to simple return)
      stepSelection('SELECT_BOX');
    }
  };

  onBoxNumSelect = e => {
    const {
      account: { cancelOrReturnItems: items },
      returns: { boxInfo },
      updateBoxNumInfo
    } = this.props;
    const boxNum = +e.target.value;
    this.setState({ boxNum });
    updateBoxNumInfo(boxNum, boxInfo, items);
  };

  onProductBoxNumSelect = e => {
    const {
      returns: { boxInfo },
      updateItemInBoxInfo
    } = this.props;
    // value in option is {boxNumSelected}-{itemUniqueId}
    // The regex says "split using just the first hyphen."
    const boxNumAndItemId = e.target.value.split(/([^-]*)-(.*)/);
    const boxNum = boxNumAndItemId[1];
    const itemId = boxNumAndItemId[2];
    updateItemInBoxInfo(boxNum, itemId, boxInfo);
  };

  onNavigateToReturnInitiate = () => {
    const {
      stepSelection,
      returns: { boxInfo },
      setIsShipmentOptionsLoading
    } = this.props;
    let boxesValid = true;
    for (const box in boxInfo) {
      if (!boxInfo[box].items.length) {
        boxesValid = false;
        break;
      }
    }
    if (boxesValid) {
      stepSelection('SELECT_LABEL');
      setIsShipmentOptionsLoading(true);
      track(() => [evNumberOfBoxesOnReturnSubmit, { numberOfBoxes: this.state.boxNum }]);
      this.context.router.pushPreserveAppRoot('/return');
    } else {
      // show error messaging that boxes aren't valid
    }
  };

  makeBoxSelection = () => {
    const {
      account: { cancelOrReturnItems: items = [] }
    } = this.props;
    const maxBoxes = items.length > RETURN_MAX_BOXES ? RETURN_MAX_BOXES : items.length;
    const options = [];
    const { boxNum } = this.state;
    const { testId } = this.context;

    if (boxNum === 0) {
      options.push(
        <option key={'PleaseSelectABox'} value={0}>
          Please make a selection
        </option>
      );
    }

    for (let i = 1; i <= maxBoxes; i++) {
      options.push(
        <option key={`howManyBoxes-${i}`} value={i}>
          {i} {pluralize('Box', i)}
        </option>
      );
    }

    return (
      <div className={css.boxNumSelection}>
        <h1 data-test-id={testId('h1')}>Items you are returning</h1>
        <ul className={css.boxInfoMsg}>
          <li>Select how many boxes you&apos;re using to ship back your return.</li>
          <li>If using more than one box, select which items go in each box.</li>
        </ul>
        <label htmlFor="boxSelect" aria-label="Select number of boxes you will be shipping back.">
          Select number of boxes
        </label>
        <select
          // Autofocus is ok in this instance, as it happens on user interaction
          // eslint-disable-next-line jsx-a11y/no-autofocus
          autoFocus
          ref={this.boxSelect}
          id="boxSelect"
          data-test-id={testId('boxSelect')}
          onChange={this.onBoxNumSelect}
        >
          {options}
        </select>
      </div>
    );
  };

  hasEmptyBoxes = () => {
    const {
      returns: { boxInfo = {} }
    } = this.props;
    const boxInfoKeys = Object.keys(boxInfo);
    const { boxNum } = this.state;

    if (boxInfoKeys.length === 0 || boxNum === 0) {
      return true;
    }

    let hasEmptyBoxes = false;
    boxInfoKeys.forEach(num => {
      const box = boxInfo[num];
      if (!box.items?.length) {
        hasEmptyBoxes = true;
      }
    });

    return hasEmptyBoxes;
  };

  makeNoReturnItemsError = () => (
    <div className={css.noReturnItems}>
      <p>You currently don't have any returnable items made within your past 10 orders.</p>
      <p>
        Please navigate to your <Link to="/orders">Order History</Link> to return items in orders placed at an earlier date.
      </p>
    </div>
  );

  render() {
    const {
      account: { cancelOrReturnItems: items },
      keepExchangeOrdersFromReturnCancel,
      returns: { step, boxInfo },
      orders: { orders },
      order
    } = this.props;

    const { boxNum } = this.state;
    const returnableOrders = (orders && extractReturnableOrders(orders, !!keepExchangeOrdersFromReturnCancel)) || [];

    // Include link back to order if navigated here from it
    const isMultiOrder = items.some(item => item.orderId !== items[0].orderId);
    const links = order?.orderId && step === 'SELECT_BOX' && !isMultiOrder ? [{ text: 'Order Information', to: `/orders/${order.orderId}` }] : [];

    if (!orders) {
      return <PageLoader centered={true} />;
    }

    return (
      <SiteAwareMetadata>
        <div className={cn(returnCss.container)}>
          <AccountBreadcrumbs links={links} className={css.breadcrumb} currentText="Returns" />
          <div className={cn(returnCss.cols, css.wrapper)}>
            <div className={returnCss.leftCol}>
              {
                // Return Items (across multiple orders) screen.
                // Navigated via /returns or clicking 'Return Items' on Main account (/account) page.
                step === 'SELECT_RETURN_ITEMS' && (
                  <>
                    <h1>Select items to return</h1>
                    {returnableOrders.length // check if orders contain returnable items
                      ? returnableOrders.map(order => {
                          const { lineItems, orderId, orderDate, returnable } = order;
                          return (
                            returnable && (
                              <ReturnProductCards
                                key={orderId}
                                orderDate={orderDate}
                                step={step}
                                boxNum={0}
                                onReturnChecked={this.onReturnChecked}
                                returnItems={items}
                                items={lineItems}
                                orderId={orderId}
                                useCustomerReturnableFlag={!!keepExchangeOrdersFromReturnCancel}
                              />
                            )
                          );
                        })
                      : this.makeNoReturnItemsError()}
                  </>
                )
              }
              {
                // Box Selection screen.
                // Navigated after selecting items from /returns or selecting items directly from Order Detail page.
                step === 'SELECT_BOX' && (
                  <>
                    {this.makeBoxSelection()}
                    <div aria-hidden={boxNum <= 1}>
                      <ReturnProductCards step={step} boxNum={boxNum} items={items} onProductBoxNumSelect={this.onProductBoxNumSelect} />
                    </div>
                  </>
                )
              }
            </div>
            <div className={cn(returnCss.rightCol, css.side)}>
              {!!items?.length && step !== 'SELECT_LABEL' && (
                <FloatingReturnItemList
                  step={step}
                  items={items}
                  boxInfo={boxInfo}
                  hasEmptyBoxes={this.hasEmptyBoxes}
                  onNavigateToBoxSelection={this.onNavigateToBoxSelection}
                  onNavigateToReturnInitiate={this.onNavigateToReturnInitiate}
                  onCancelReturn={this.onCancelReturn}
                />
              )}
            </div>
          </div>
        </div>
      </SiteAwareMetadata>
    );
  }
}

function mapStateToProps(state) {
  const {
    account,
    killswitch: { keepExchangeOrdersFromReturnCancel, showBatchReturn },
    orders,
    order: { order },
    returns
  } = state;

  return {
    account,
    keepExchangeOrdersFromReturnCancel,
    showBatchReturn,
    returns,
    orders,
    order
  };
}

export default connect(mapStateToProps, {
  fetchOrders,
  removeItemForCancellationOrReturn,
  resetReturnsInState,
  selectItemForCancellationOrReturn,
  stepSelection,
  updateBoxNumInfo,
  updateItemInBoxInfo,
  setIsShipmentOptionsLoading
})(ReturnAll);
